import { z } from 'zod';
import { DEFAULT_FALLBACK_LANGUAGE, SUPPORTED_LANGUAGES, isSupportedLanguage, normalizeLocaleToLanguage, } from '../locale.js';
import { CDLIT_NON_NONE_SAFETY_FLAGS } from './safety-flags.js';
export const AI_GUIDED_CONTENT_SCHEMA_VERSION = 1;
export const aiGuidedContentEmphasisSchema = z.enum(['primary', 'secondary', 'ghost']);
export const aiGuidedMessageVariantSchema = z
    .object({
    id: z.string().min(1).max(64),
    text: z.string().min(1).max(4000),
})
    .strict();
export const aiGuidedOptionRefSchema = z
    .object({
    optionId: z.string().min(1).max(64),
    title: z.string().min(1).max(500),
    description: z.string().max(1000).optional(),
    iconKey: z.string().max(64).optional(),
    emphasis: aiGuidedContentEmphasisSchema.optional(),
    order: z.number().int().min(0).max(999).optional(),
    visible: z.boolean().optional(),
})
    .strict();
export const aiGuidedAssistOptionRefSchema = z
    .object({
    assistOptionId: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    emphasis: aiGuidedContentEmphasisSchema.optional(),
    order: z.number().int().min(0).max(999).optional(),
    visible: z.boolean().optional(),
})
    .strict();
export const aiGuidedStepContentSchema = z
    .object({
    messageVariants: z.array(aiGuidedMessageVariantSchema).min(1).max(32),
    optionRefs: z.array(aiGuidedOptionRefSchema).max(64).default([]),
    assistOptionRefs: z.array(aiGuidedAssistOptionRefSchema).max(16).default([]),
})
    .strict();
export const aiGuidedLocaleBundleSchema = z
    .object({
    panel: z
        .object({
        title: z.string().min(1).max(200),
        lead: z.string().max(500).optional(),
    })
        .strict(),
    steps: z.record(z.string().min(1).max(64), aiGuidedStepContentSchema),
    privacyHint: z.string().max(500).optional(),
    bookingReview: z
        .object({
        staffLabel: z.string().min(1).max(100),
        timeLabel: z.string().min(1).max(100),
    })
        .strict()
        .optional(),
    contactForm: z
        .object({
        nameLabel: z.string().min(1).max(100),
        phoneLabel: z.string().min(1).max(100),
        emailLabel: z.string().min(1).max(100),
        submitLabel: z.string().min(1).max(200),
    })
        .strict()
        .optional(),
})
    .strict();
export const aiGuidedBookingMasterCommentSchema = z
    .object({
    sourceLine: z.string().min(1).max(200),
    serviceLineTemplate: z.string().min(1).max(500),
    safetyLineTemplate: z.string().max(500).optional(),
    safetyFlagLabels: z.record(z.string().min(1).max(64), z.string().min(1).max(200)).optional(),
})
    .strict();
const supportedLanguageSchema = z.enum(SUPPORTED_LANGUAGES);
export const aiGuidedContentSchema = z
    .object({
    schemaVersion: z.literal(AI_GUIDED_CONTENT_SCHEMA_VERSION),
    defaultLocale: supportedLanguageSchema,
    locales: z.record(supportedLanguageSchema, aiGuidedLocaleBundleSchema),
    bookingMasterComment: aiGuidedBookingMasterCommentSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    for (const key of Object.keys(value.locales)) {
        if (!isSupportedLanguage(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Unsupported locale key: ${key}`,
                path: ['locales', key],
            });
        }
    }
});
export class GuidedContentError extends Error {
    constructor(message, code) {
        super(message);
        this.code = code;
        this.name = 'GuidedContentError';
    }
}
export function parseGuidedContent(raw) {
    const parsed = aiGuidedContentSchema.safeParse(raw);
    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        throw new GuidedContentError(issue?.message ?? 'Invalid guided content', 'guided_content_invalid');
    }
    return parsed.data;
}
function assertUniqueIds(ids, label) {
    const seen = new Set();
    for (const id of ids) {
        if (seen.has(id)) {
            throw new GuidedContentError(`Duplicate ${label}: ${id}`, 'guided_duplicate_id');
        }
        seen.add(id);
    }
}
export function validateGuidedContentDocument(content, catalog, assistCatalog = {}) {
    if (!content.locales.ru) {
        throw new GuidedContentError('Russian locale (ru) is required for publish', 'guided_content_missing_ru');
    }
    for (const [localeKey, bundle] of Object.entries(content.locales)) {
        if (!isSupportedLanguage(localeKey)) {
            throw new GuidedContentError(`Unsupported locale: ${localeKey}`, 'guided_content_invalid');
        }
        for (const [stepKey, step] of Object.entries(bundle.steps)) {
            assertUniqueIds(step.messageVariants.map((v) => v.id), `messageVariants in ${localeKey}/${stepKey}`);
            assertUniqueIds(step.optionRefs.map((o) => o.optionId), `optionRefs in ${localeKey}/${stepKey}`);
            assertUniqueIds(step.assistOptionRefs.map((o) => o.assistOptionId), `assistOptionRefs in ${localeKey}/${stepKey}`);
            for (const optionRef of step.optionRefs) {
                const catalogEntry = catalog[optionRef.optionId];
                if (!catalogEntry) {
                    throw new GuidedContentError(`Unknown optionId ${optionRef.optionId} in ${localeKey}/${stepKey}`, 'guided_catalog_unknown_option');
                }
                if (!catalogEntry.allowedSteps.includes(stepKey)) {
                    throw new GuidedContentError(`optionId ${optionRef.optionId} not allowed on step ${stepKey}`, 'guided_catalog_step_mismatch');
                }
            }
            for (const assistRef of step.assistOptionRefs) {
                const assistEntry = assistCatalog[assistRef.assistOptionId];
                if (!assistEntry) {
                    throw new GuidedContentError(`Unknown assistOptionId ${assistRef.assistOptionId} in ${localeKey}/${stepKey}`, 'guided_catalog_unknown_assist_option');
                }
                if (!assistEntry.allowedSteps.includes(stepKey)) {
                    throw new GuidedContentError(`assistOptionId ${assistRef.assistOptionId} not allowed on step ${stepKey}`, 'guided_catalog_step_mismatch');
                }
            }
        }
    }
    const master = content.bookingMasterComment;
    if (master?.safetyLineTemplate?.trim()) {
        const labels = master.safetyFlagLabels ?? {};
        for (const flag of CDLIT_NON_NONE_SAFETY_FLAGS) {
            if (!labels[flag]?.trim()) {
                throw new GuidedContentError(`bookingMasterComment.safetyFlagLabels missing label for ${flag}`, 'guided_content_invalid');
            }
        }
    }
}
export function resolveGuidedLocale(content, sessionLocale) {
    const preferred = sessionLocale ? normalizeLocaleToLanguage(sessionLocale) : null;
    if (preferred && content.locales[preferred])
        return preferred;
    if (content.locales[content.defaultLocale])
        return content.defaultLocale;
    if (content.locales.ru)
        return 'ru';
    const first = Object.keys(content.locales).find(isSupportedLanguage);
    return first ?? DEFAULT_FALLBACK_LANGUAGE;
}
/** Deterministic variant pick — stable per session + published config version. */
export function pickStableGreetingVariantId(sessionId, configVersion, variantIds) {
    if (variantIds.length === 0) {
        throw new GuidedContentError('opening step requires messageVariants', 'guided_content_invalid');
    }
    if (variantIds.length === 1)
        return variantIds[0];
    let hash = 0;
    const key = `${sessionId}:${configVersion}`;
    for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return variantIds[hash % variantIds.length];
}
const GREETING_TZ = 'Europe/Moscow';
/** Time-of-day bucket by Moscow local hour (matches `g-morning-* / g-day-* / g-evening-*` seed ids). */
export function resolveGreetingTimeBucket(hour) {
    if (hour >= 6 && hour < 12)
        return 'morning';
    if (hour >= 12 && hour < 18)
        return 'day';
    return 'evening';
}
function moscowHour(now) {
    const formatted = new Intl.DateTimeFormat('en-US', {
        timeZone: GREETING_TZ,
        hour: 'numeric',
        hour12: false,
    }).format(now);
    const hour = Number(formatted);
    return Number.isFinite(hour) ? hour % 24 : now.getHours();
}
/**
 * Time-aware greeting pick: restricts the stable-hash pool to variant ids prefixed
 * `g-{bucket}` for the current Moscow hour, falling back to the full pool when the
 * content has no variants for that bucket (e.g. legacy/custom content).
 */
export function selectGreetingVariantForNow(sessionId, configVersion, variantIds, now = new Date()) {
    const bucket = resolveGreetingTimeBucket(moscowHour(now));
    const bucketIds = variantIds.filter((id) => id.startsWith(`g-${bucket}`));
    const pool = bucketIds.length > 0 ? bucketIds : variantIds;
    return pickStableGreetingVariantId(sessionId, configVersion, pool);
}
export const aiGuidedPresentationOptionSchema = z
    .object({
    optionId: z.string().min(1).max(64),
    title: z.string().min(1).max(500),
    description: z.string().max(1000).optional(),
    iconKey: z.string().max(64).optional(),
    emphasis: aiGuidedContentEmphasisSchema.optional(),
})
    .strict();
export const aiGuidedPresentationAssistOptionSchema = z
    .object({
    assistOptionId: z.string().min(1).max(64),
    title: z.string().min(1).max(200),
    emphasis: aiGuidedContentEmphasisSchema.optional(),
    requiresText: z.boolean().optional(),
})
    .strict();
export const aiGuidedPresentationSchema = z
    .object({
    locale: supportedLanguageSchema,
    configVersion: z.number().int().positive(),
    greetingVariantId: z.string().min(1).max(64).optional(),
    step: z.string().min(1).max(64),
    panelTitle: z.string().min(1).max(200),
    panelLead: z.string().max(500).optional(),
    message: z.string().min(1).max(4000),
    options: z.array(aiGuidedPresentationOptionSchema),
    assistOptions: z.array(aiGuidedPresentationAssistOptionSchema).optional(),
    privacyHint: z.string().max(500).optional(),
    bookingReview: aiGuidedLocaleBundleSchema.shape.bookingReview.optional(),
    contactForm: aiGuidedLocaleBundleSchema.shape.contactForm.optional(),
})
    .strict();
export function resolveGuidedPresentation(input) {
    const locale = resolveGuidedLocale(input.content, input.sessionLocale);
    const bundle = input.content.locales[locale];
    if (!bundle) {
        throw new GuidedContentError(`Missing locale bundle: ${locale}`, 'guided_content_invalid');
    }
    const stepContent = bundle.steps[input.step];
    const message = stepContent
        ? resolveStepMessage(stepContent, input.step, input.sessionId, input.configVersion, input.greetingVariantId)
        : '';
    const options = (stepContent?.optionRefs ?? [])
        .filter((ref) => ref.visible !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((ref) => ({
        optionId: ref.optionId,
        title: ref.title,
        ...(ref.description ? { description: ref.description } : {}),
        ...(ref.iconKey ? { iconKey: ref.iconKey } : {}),
        ...(ref.emphasis ? { emphasis: ref.emphasis } : {}),
    }));
    const assistOptions = (stepContent?.assistOptionRefs ?? [])
        .filter((ref) => ref.visible !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((ref) => {
        const catalogEntry = input.assistCatalog?.[ref.assistOptionId];
        return {
            assistOptionId: ref.assistOptionId,
            title: ref.title,
            ...(ref.emphasis ? { emphasis: ref.emphasis } : {}),
            ...(catalogEntry?.requiresText ? { requiresText: true } : {}),
        };
    });
    return aiGuidedPresentationSchema.parse({
        locale,
        configVersion: input.configVersion,
        step: input.step,
        panelTitle: bundle.panel.title,
        panelLead: bundle.panel.lead,
        message,
        options,
        ...(input.step === 'opening' && input.greetingVariantId
            ? { greetingVariantId: input.greetingVariantId }
            : {}),
        ...(assistOptions.length > 0 ? { assistOptions } : {}),
        privacyHint: bundle.privacyHint,
        bookingReview: bundle.bookingReview,
        contactForm: bundle.contactForm,
    });
}
function resolveStepMessage(stepContent, step, sessionId, configVersion, greetingVariantId) {
    if (step === 'opening') {
        const ids = stepContent.messageVariants.map((v) => v.id);
        const requested = greetingVariantId && ids.includes(greetingVariantId) ? greetingVariantId : null;
        const variantId = requested ?? selectGreetingVariantForNow(sessionId, configVersion, ids);
        const variant = stepContent.messageVariants.find((v) => v.id === variantId);
        if (!variant) {
            throw new GuidedContentError(`Unknown greeting variant: ${variantId}`, 'guided_content_invalid');
        }
        return variant.text;
    }
    const first = stepContent.messageVariants[0];
    if (!first) {
        throw new GuidedContentError(`Step ${step} requires messageVariants`, 'guided_content_invalid');
    }
    return first.text;
}
export function resolveCatalogAdvance(catalog, optionId, step) {
    const entry = catalog[optionId];
    if (!entry) {
        throw new GuidedContentError(`Unknown optionId: ${optionId}`, 'guided_catalog_unknown_option');
    }
    if (!entry.allowedSteps.includes(step)) {
        throw new GuidedContentError(`optionId ${optionId} not allowed on step ${step}`, 'guided_catalog_step_mismatch');
    }
    return { action: entry.action, payload: entry.payload };
}
export function resolveAssistCatalogRequest(catalog, assistOptionId, step, text) {
    const entry = catalog[assistOptionId];
    if (!entry) {
        throw new GuidedContentError(`Unknown assistOptionId: ${assistOptionId}`, 'guided_catalog_unknown_assist_option');
    }
    if (!entry.allowedSteps.includes(step)) {
        throw new GuidedContentError(`assistOptionId ${assistOptionId} not allowed on step ${step}`, 'guided_catalog_step_mismatch');
    }
    if (entry.kind === 'other') {
        const normalized = text?.trim();
        if (!normalized) {
            throw new GuidedContentError('Assist text is required', 'guided_content_invalid');
        }
        return { kind: 'other', text: normalized };
    }
    if (entry.kind === 'objection') {
        if (!entry.objection) {
            throw new GuidedContentError(`Missing objection for ${assistOptionId}`, 'guided_content_invalid');
        }
        return { kind: 'objection', objection: entry.objection };
    }
    if (!entry.reason) {
        throw new GuidedContentError(`Missing handoff reason for ${assistOptionId}`, 'guided_content_invalid');
    }
    return { kind: 'handoff', reason: entry.reason };
}
//# sourceMappingURL=guided-content.js.map